import { UserWithActiveStatusResponse, deactivateUser, activateUser, resetUserPassword, sendUserOffline } from "../../services/userService";
import { Text, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow, Badge, IconEdit, Box, Inline, TooltipRenderer, Stack, TextField, Dropdown, Column, Columns, Button, IconAdd, Actions, IconCopy, IconDisallow, IconDesktop, IconLinkBroken, IconCaution } from "braid-design-system"
import React, { useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useToast } from "../../hooks/useToast";
import { useUserPermissionAndMenu } from "../../contexts/UserPermissionAndMenuProvider";

interface UsersListTableProps {
    userList?: Array<UserWithActiveStatusResponse> | null;
    onEditUser?: (userId: number) => void;
    onCreateUser?: () => void;
    onUserUpdated?: () => void;
}

const UsersListTable: React.FC<UsersListTableProps> = ({
    userList,
    onEditUser,
    onCreateUser,
    onUserUpdated,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedBranch, setSelectedBranch] = useState<string>('');
    const toast = useToast();
    const { t } = useTranslation();
    const { hasPermission } = useUserPermissionAndMenu();
    const [actionInProgress, setActionInProgress] = useState<number | null>(null);
    const showActions = typeof onEditUser === "function";
    const canManageUsers = hasPermission("USER:MANAGE");

    // Get unique branches for dropdown
    const branches = useMemo(() => {
        if (!userList) return [];
        const branchMap = new Map<string, string>();
        userList.forEach((user) => {
            const branchCode = String(user.active_branch?.loc_code ?? "").trim();
            if (branchCode) {
                const branchLabel = String(user.active_branch?.loc_desc ?? branchCode).trim() || branchCode;
                branchMap.set(branchCode, branchLabel);
            }
        });

        return [
            { value: "", label: t('page.user_management.list.branch_filter.all', { defaultValue: 'All Branch' }) },
            ...Array.from(branchMap.entries())
                .sort((left, right) => left[1].localeCompare(right[1]))
                .map(([value, label]) => ({ value, label }))
        ];
    }, [userList, t]);

    // Filter users based on search term and branch
    const filteredUsers = useMemo(() => {
        if (!userList) return userList;

        const filtered = userList.filter((user) => {
            const matchesSearch = !searchTerm.trim() || (
                user.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            const matchesBranch = !selectedBranch || String(user.active_branch?.loc_code ?? "").trim() === selectedBranch;

            return matchesSearch && matchesBranch;
        });

        // Sort by online_status first (true first, then false)
        return filtered.sort((a, b) => {
            if (a.online_status === b.online_status) {
                return 0;
            }
            return a.online_status ? -1 : 1;
        });
    }, [userList, searchTerm, selectedBranch]);

    const handleDeactivateUser = async (userId: number) => {
        try {
            setActionInProgress(userId);
            await deactivateUser(userId);
            toast.showSuccess(t('page.user_management.list.toast.deactivated', { defaultValue: 'User deactivated successfully' }));
            if (onUserUpdated) {
                onUserUpdated();
            }
        } catch (error: any) {
            toast.showError(t('page.user_management.list.error.deactivate', { defaultValue: 'Error deactivating user: {{message}}', message: error?.message || 'Unknown error' }));
        } finally {
            setActionInProgress(null);
        }
    };

    const handleActivateUser = async (userId: number) => {
        try {
            setActionInProgress(userId);
            await activateUser(userId);
            toast.showSuccess(t('page.user_management.list.toast.activated', { defaultValue: 'User activated successfully' }));
            if (onUserUpdated) {
                onUserUpdated();
            }
        } catch (error: any) {
            toast.showError(t('page.user_management.list.error.activate', { defaultValue: 'Error activating user: {{message}}', message: error?.message || 'Unknown error' }));
        } finally {
            setActionInProgress(null);
        }
    };

    const handleResetPassword = async (userId: number) => {
        try {
            setActionInProgress(userId);
            const resetUrl = await resetUserPassword(userId);
            try {
                await navigator.clipboard.writeText(resetUrl);
                toast.showSuccess(t('page.user_management.list.toast.reset_link_copied', { defaultValue: 'Password reset link generated and copied to clipboard' }));
            } catch {
                toast.showSuccess(t('page.user_management.list.toast.reset_link', { defaultValue: 'Password reset link generated successfully' }));
            }
            if (onUserUpdated) {
                onUserUpdated();
            }
        } catch (error: any) {
            toast.showError(t('page.user_management.list.error.reset', { defaultValue: 'Error resetting password: {{message}}', message: error?.message || 'Unknown error' }));
        } finally {
            setActionInProgress(null);
        }
    };

    const handleSendOffline = async (userId: number) => {
        try {
            setActionInProgress(userId);
            await sendUserOffline(userId);
            toast.showSuccess(t('page.user_management.list.toast.sent_offline', { defaultValue: 'User sent offline successfully' }));
            if (onUserUpdated) {
                onUserUpdated();
            }
        } catch (error: any) {
            toast.showError(t('page.user_management.list.error.send_offline', { defaultValue: 'Error sending user offline: {{message}}', message: error?.message || 'Unknown error' }));
        } finally {
            setActionInProgress(null);
        }
    };

    return (
        <>
            <Stack space="large" >
                <Columns space="large" collapseBelow="tablet">
                    <Column width="1/3">
                        <Box display={"flex"} justifyContent="flexStart">
                            {onCreateUser ? (
                                <Actions>
                                    <Button
                                        variant="ghost"
                                        tone="brandAccent"
                                        icon={<IconAdd />}
                                        onClick={onCreateUser}
                                    >
                                        {t('page.user_management.list.button.create_new', { defaultValue: 'Create New User' })}
                                    </Button>
                                </Actions>
                            ) : null}
                        </Box>
                    </Column>
                    <Column width="2/3">
                        <Box display={"flex"} justifyContent="flexEnd" alignItems="center" gap="medium">
                            <TextField
                                placeholder={t('page.user_management.list.search.placeholder', { defaultValue: 'Search Users...' })}
                                aria-label={t('page.user_management.list.search.aria', { defaultValue: 'Search Users' })}
                                onChange={(e) => {
                                    setSearchTerm(e.currentTarget.value);
                                }}
                                value={searchTerm}
                            />
                            <Dropdown
                                id="branch-filter"
                                aria-label={t('page.user_management.list.branch_filter.aria', { defaultValue: 'Branch Filter' })}
                                value={selectedBranch}
                                onChange={(e) => setSelectedBranch(e.currentTarget.value)}
                            >

                                {branches.map((branch) => (
                                    <option key={branch.value} value={branch.value}>{branch.label}</option>
                                ))}

                            </Dropdown>
                        </Box>
                    </Column>
                </Columns>
                <Table label={t('page.user_management.list.table.label', { defaultValue: 'Users List Table' })}>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell><Text>{t('page.user_management.list.table.headers.username', { defaultValue: 'Username' })}</Text></TableHeaderCell>
                            <TableHeaderCell width="20%"><Text>{t('page.user_management.list.table.headers.full_name', { defaultValue: 'Full Name' })}</Text></TableHeaderCell>
                            <TableHeaderCell><Text>{t('page.user_management.list.table.headers.branch', { defaultValue: 'Branch' })}</Text></TableHeaderCell>
                            <TableHeaderCell><Text>{t('page.user_management.list.table.headers.group', { defaultValue: 'Group' })}</Text></TableHeaderCell>
                            <TableHeaderCell align="center"><Text>{t('page.user_management.list.table.headers.password_status', { defaultValue: 'Password Status' })}</Text></TableHeaderCell>
                            <TableHeaderCell align="center"><Text>{t('page.user_management.list.table.headers.status', { defaultValue: 'Status' })}</Text></TableHeaderCell>
                            <TableHeaderCell align="center"><Text>{t('page.user_management.list.table.headers.online_status', { defaultValue: 'Online Status' })}</Text></TableHeaderCell>
                            {showActions ? <TableHeaderCell align="right"><Text>{t('page.user_management.list.table.headers.actions', { defaultValue: 'Actions' })}</Text></TableHeaderCell> : null}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {
                            filteredUsers && filteredUsers.length > 0 ? (
                                filteredUsers.map((user) => (
                                    <TableRow key={user.user_id}>
                                        <TableCell><Text>{user.user_name}</Text></TableCell>
                                        <TableCell><Text>{user.name}</Text></TableCell>
                                        <TableCell><Text>{String(user.active_branch?.loc_desc ?? user.active_branch?.loc_code ?? t('page.user_management.list.table.na', { defaultValue: 'N/A' }))}</Text></TableCell>
                                        <TableCell><Text>{user.user_group?.group_name || t('page.user_management.list.table.na', { defaultValue: 'N/A' })}</Text></TableCell>
                                        <TableCell align="center">
                                            {
                                                user.password_status === 1 ?
                                                    <Badge tone="positive">{t('page.user_management.list.badge.active', { defaultValue: 'Active' })}</Badge> :
                                                    <Badge tone="caution">{t('page.user_management.list.badge.inactive', { defaultValue: 'Inactive' })}</Badge>
                                            }

                                        </TableCell>
                                        <TableCell align="center">
                                            {user.status === 1 ?
                                                <Badge tone="positive">{t('page.user_management.list.badge.active', { defaultValue: 'Active' })}</Badge> :
                                                <Badge tone="caution">{t('page.user_management.list.badge.inactive', { defaultValue: 'Inactive' })}</Badge>
                                            }
                                        </TableCell>
                                        <TableCell align="center">
                                            {user.online_status ?
                                                <Badge tone="positive">{t('page.user_management.list.badge.online', { defaultValue: 'Online' })}</Badge> :
                                                <Badge tone="neutral">{t('page.user_management.list.badge.offline', { defaultValue: 'Offline' })}</Badge>
                                            }
                                        </TableCell>
                                        {showActions ? (
                                            <TableCell align="right">
                                                <Inline space="small">
                                                    <TooltipRenderer tooltip={<Text>{t('page.user_management.list.tooltip.edit', { defaultValue: 'Edit User' })}</Text>}>
                                                        {
                                                            ({ triggerProps }) => (
                                                                <Box onClick={() => onEditUser?.(user.user_id)} cursor="pointer" {...triggerProps}>
                                                                    <IconEdit tone="info" />
                                                                </Box>)
                                                        }
                                                    </TooltipRenderer>

                                                    <TooltipRenderer tooltip={<Text>{t('page.user_management.list.tooltip.send_offline', { defaultValue: 'Send User Offline' })}</Text>}>
                                                        {
                                                            ({ triggerProps }) => (
                                                                <Box
                                                                    onClick={() => handleSendOffline(user.user_id)}
                                                                    cursor={actionInProgress === user.user_id ? "default" : "pointer"}
                                                                    {...triggerProps}
                                                                >
                                                                    <IconLinkBroken tone="secondary" />
                                                                </Box>)
                                                        }
                                                    </TooltipRenderer>
                                                    {user.reset_password_url ? (
                                                        <TooltipRenderer tooltip={<Text>{t('page.user_management.list.tooltip.copy_reset_url', { defaultValue: 'Copy User Password Reset URL' })}</Text>}>
                                                            {
                                                                ({ triggerProps }) => (
                                                                    <Box
                                                                        onClick={async () => {
                                                                            await navigator.clipboard.writeText(user.reset_password_url!);
                                                                            toast.showSuccess(t('page.user_management.list.toast.reset_url_copied', { defaultValue: 'Reset URL copied to clipboard' }));
                                                                        }}
                                                                        cursor="pointer" {...triggerProps}>
                                                                        <IconCopy tone="promote" />
                                                                    </Box>)
                                                            }
                                                        </TooltipRenderer>
                                                    ) : null
                                                    }
                                                    {canManageUsers ? (
                                                        <TooltipRenderer tooltip={<Text>{t('page.user_management.list.tooltip.request_reset', { defaultValue: 'Request Password Reset' })}</Text>}>
                                                            {
                                                                ({ triggerProps }) => (
                                                                    <Box
                                                                        onClick={() => handleResetPassword(user.user_id)}
                                                                        cursor={actionInProgress === user.user_id ? "default" : "pointer"}
                                                                        {...triggerProps}
                                                                    >
                                                                        <IconCaution tone="caution" />
                                                                    </Box>)
                                                            }
                                                        </TooltipRenderer>
                                                    ) : null}
                                                    {user.status === 1 ? (
                                                        <TooltipRenderer tooltip={<Text>{t('page.user_management.list.tooltip.deactivate', { defaultValue: 'Deactivate User' })}</Text>}>
                                                            {
                                                                ({ triggerProps }) => (
                                                                    <Box
                                                                        onClick={() => handleDeactivateUser(user.user_id)}
                                                                        cursor={actionInProgress === user.user_id ? "default" : "pointer"}
                                                                        {...triggerProps}
                                                                    >
                                                                        <IconDisallow tone="critical" />
                                                                    </Box>)
                                                            }
                                                        </TooltipRenderer>
                                                    ) : (
                                                        <TooltipRenderer tooltip={<Text>{t('page.user_management.list.tooltip.activate', { defaultValue: 'Activate User' })}</Text>}>
                                                            {
                                                                ({ triggerProps }) => (
                                                                    <Box
                                                                        onClick={() => handleActivateUser(user.user_id)}
                                                                        cursor={actionInProgress === user.user_id ? "default" : "pointer"}
                                                                        {...triggerProps}
                                                                    >
                                                                        <IconDesktop tone="positive" />
                                                                    </Box>)
                                                            }
                                                        </TooltipRenderer>
                                                    )}
                                                </Inline>
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colspan={showActions ? 8 : 7} align="center">
                                        <Text>{t('page.user_management.list.empty', { defaultValue: 'No users found.' })}</Text>
                                    </TableCell>
                                </TableRow>
                            )

                        }

                    </TableBody>
                </Table>
            </Stack>
        </>
    )
}

export default UsersListTable;
